import { ChangeDetectionStrategy, Component, DestroyRef, HostListener, computed, effect, inject, signal, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router, NavigationEnd, RouterOutlet } from '@angular/router';
import { filter, take } from 'rxjs';
import type { PostHogConfig } from 'posthog-js';
import type { ConsentListener } from '@ricohproducts/consent';
import { ConsentService } from '@ricohproducts/consent/angular';
import { CallDockHostComponent } from './features/calls/dock/call-dock-host.component';
import { AppShellComponent } from './features/shell/app-shell.component';
import { AppUpdatePillComponent } from './features/shell/components/app-update-pill/app-update-pill.component';
import { AppUpdateService } from './core/services/app-update.service';
import { isUnattendedSignageUrl } from './core/utils/unattended-route';
import { TeamsAuthService } from './core/services/teams-auth.service';
import { TeamsService } from './core/services/teams.service';
import { CateringBookingSyncService } from './core/services/catering-booking-sync.service';
import { PosthogService } from './core/services/posthog.service';
import { AnalyticsService } from './core/services/analytics.service';
import { realtimeWsFailed$ } from './core/services/realtime-connection';
import { wsConfigHealth } from './core/services/ws-config-health';
import { createWsFailureThrottle } from './core/services/ws-failure-throttle';
import { redactSignageTokens } from './core/analytics/redact-signage-token';
import { redactJoinCodes } from './core/analytics/redact-join-code';
import { redactHubJoinTokenString, redactHubJoinTokens } from './core/analytics/redact-hub-join-token';
import { isMeetingJoinSurface, JOIN_SURFACE_POSTHOG_OPTIONS } from './core/analytics/join-surface-analytics';
import { dropsReplaySnapshot, FlowReplayGate } from './core/analytics/flow-replay-exclusion';
import { isFlowJoinSurface } from './core/utils/flow-join-surface';
import { chainNamesReplayMask } from './core/analytics/replay-mask-chain';
import {
  parseSignageHandoffHash,
  signageHandoffDedupeKey,
} from './features/signage/kernel/signage-handoff';
import { ViewerService } from './core/services/viewer.service';
import { LocationStateService } from './core/services/location-state.service';
import { QuickLinksService } from './core/services/quick-links.service';
import { CalendarCapabilityService } from './core/services/calendar-capability.service';
import { ThemeService } from './core/services/theme.service';
import { UpdaterService } from './core/services/updater.service';
import { PwaInstallService } from './core/services/pwa-install.service';
import { currentDisplayMode } from './core/utils/pwa';
import {
  isSignInProblemSurface,
  isUnauthenticatedSurface,
} from './core/utils/unauthenticated-surface';
import { RegionService } from './core/services/region.service';
import { BrandConfigService } from './core/services/brand-config.service';
import { getSelectedRegion, getWsUrl, regionGateActive } from './core/region';
import { isDesktopSignInSurface } from './core/desktop-auth';
import { isFlowWidgetSurface } from './core/desktop-flow';
import { DesktopAuthService } from './core/services/desktop-auth.service';
import { DesktopWelcomeComponent } from './features/desktop-welcome/desktop-welcome.component';
import { ANALYTICS_EVENTS } from './core/analytics/events';
import { environmentTier } from './core/analytics/environment-tier';
import {
  deriveDomainFromRoute,
  deriveRouteName,
} from './core/analytics/route-taxonomy';
import { environment } from '../environments/environment';
import {
  isTauri,
  openExternal,
  attachTauriConsoleForwarding,
  watchOsTheme,
} from './core/tauri-runtime';

/** Coarse breakpoint bucket so we can segment events by device class without
 *  shipping the exact viewport on every event. Aligns with our SCSS breakpoints. */
function breakpointFor(width: number): 'xs' | 'sm' | 'md' | 'lg' | 'xl' {
  if (width < 480) return 'xs';
  if (width < 768) return 'sm';
  if (width < 1024) return 'md';
  if (width < 1440) return 'lg';
  return 'xl';
}

/**
 * Parse UTM / acquisition params out of a URL + referrer and split into:
 *  - `session`: super-properties tagged onto every event in this session
 *    (drop the `initial_` prefix so funnels read cleanly)
 *  - `initial`: $set_once person properties, prefixed `initial_*` so they
 *    pin the *first-ever* acquisition source per viewer and are never
 *    overwritten by a later visit
 *
 * Recognised keys: standard UTM (utm_source/medium/campaign/term/content),
 * Google click id (gclid), Microsoft click id (msclkid), Facebook (fbclid),
 * plus our internal shorthand (`ref`, `campaign`, `source`) and a synthetic
 * `referring_domain` derived from document.referrer.
 */
function readCampaignParams(search: string, referrer: string): {
  hasAny: boolean;
  session: Record<string, string>;
  initial: Record<string, string>;
} {
  const params = new URLSearchParams(search);
  const session: Record<string, string> = {};
  const initial: Record<string, string> = {};
  const keys = [
    'utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content',
    'gclid', 'msclkid', 'fbclid',
    // Internal short forms used by email/Teams deep links into the app.
    'ref', 'campaign', 'source',
  ];
  for (const k of keys) {
    const v = params.get(k);
    if (v) {
      session[k] = v;
      initial[`initial_${k}`] = v;
    }
  }
  let referringDomain: string | undefined;
  if (referrer) {
    try {
      referringDomain = new URL(referrer).hostname;
    } catch { /* malformed referrer */ }
  }
  if (referringDomain && !session['utm_source']) {
    // Only fall back to referrer when no explicit UTM was supplied —
    // explicit campaign tags always win.
    session['referring_domain'] = referringDomain;
    initial['initial_referring_domain'] = referringDomain;
  }
  return {
    hasAny: Object.keys(session).length > 0,
    session,
    initial,
  };
}

/**
 * Outlook add-in surfaces — the task pane route AND its preview harness.
 * Both render bare (no employee shell): a 320–450px docked pane has no
 * room for nav chrome, and the harness frames the pane itself.
 */
function isAddinUrl(url: string): boolean {
  return url.startsWith('/addin') || url.startsWith('/preview/addin-rooms');
}

/** Remove entity ids from analytics/replay route dimensions. */
function canonicalRoutePath(url: string): string {
  // No fragment after the route (`/schedule#rt=…` is a sign-in handoff); a
  // leading `#` is the hash route itself and stays.
  const path = url.split('?')[0].replace(/(?!^)#.*$/, '');
  return path
    .replace(/^\/people\/[^/]+/, '/people/:id')
    .replace(/^\/games\/c\/[^/]+/, '/games/c/:id');
}

/**
 * Strip volatile query params from the hash so route-level pageviews
 * don't fragment across theme toggles, panel state, or selection ids.
 * Keeps the route + pathname; drops everything after `?` on the hash.
 * Used as the `$current_url` we send to PostHog so the Activity feed
 * surfaces clean URLs instead of `…/map?aesthetic=clean&space=abc`.
 */
function cleanCurrentUrl(href: string): string {
  try {
    const u = new URL(href);
    const hash = u.hash;
    const hashRoute = canonicalRoutePath(hash.split('?')[0]);
    return `${u.origin}${u.pathname}${hashRoute}`;
  } catch {
    return href;
  }
}

/** The join page, or one of its preview harnesses (bare: no employee shell). */
function isFlowJoinBareRoute(url: string): boolean {
  const route = url.startsWith('#') ? url.slice(1) : url;
  return (
    isFlowJoinSurface(route) ||
    route.startsWith('/preview/flow-join') ||
    route.startsWith('/preview/call-stage')
  );
}

@Component({
  changeDetection: ChangeDetectionStrategy.Default,
  selector: 'im-root',
  imports: [AppShellComponent, AppUpdatePillComponent, RouterOutlet, DesktopWelcomeComponent, CallDockHostComponent],
  template: `
    @if (showRegionWelcome()) {
      <!-- Desktop first-run: pick a data region BEFORE any backend call. -->
      <im-desktop-welcome />
    } @else if (isBareRoute()) {
      <router-outlet />
      <!-- Bare routes bypass the employee shell (and its update banner), so
           they get the floating pill instead. Signage is excluded: attended
           signage pages are short-lived, and unattended player displays
           auto-reload onto new builds (see the auto-apply effect below). -->
      @if (!isSignageRoute() && !isFlowWidgetRoute()) {
        <im-app-update-pill />
      }
    } @else if (teamsAuth.authFailed()) {
      <div class="teams-auth-error">
        <div class="teams-auth-card">
          <h2>Sign in required</h2>
          <p>Automatic sign-in couldn't complete. Sign in with your work account to continue.</p>
          <!-- Host-brokered NAA popup (works inside the Teams/Outlook webview,
               where an external login-web tab can't hand tokens back). -->
          <button
            type="button"
            class="teams-auth-btn ph-no-capture"
            [disabled]="teamsSigningIn()"
            (click)="teamsInteractiveSignIn()"
          >
            {{ teamsSigningIn() ? 'Signing in…' : 'Sign in with Microsoft' }}
          </button>
          <a class="teams-auth-link" [href]="loginUrl" target="_blank" rel="noopener">
            Having trouble? Open RICOH Workplace in your browser
          </a>
          <details class="teams-auth-diag">
            <summary>Diagnostics</summary>
            <!-- ph-mask: session replay must not record diagnostic details -->
            <pre class="ph-mask">{{ teamsAuth.diagnostics().join('\n') }}</pre>
          </details>
        </div>
      </div>
    } @else {
      <im-app-shell />
    }
    @if (showCallDock()) {
      <!-- Flow Live: the call dock and the incoming-call card float over every page, outside the routes, so a
           call survives navigation (0sm8.3). Not over a meeting's own join page, a widget, signage or the add-in. -->
      <im-call-dock-host [aboveNav]="!isBareRoute()" />
    }
  `,
  styles: `
    .teams-auth-error {
      display: flex;
      align-items: center;
      justify-content: center;
      height: 100vh;
      background: var(--bg, #0f0f11);
      color: var(--text, #e5e5e5);
      font-family: inherit;
    }
    .teams-auth-card {
      text-align: center;
      max-width: 360px;
      padding: 40px;
    }
    .teams-auth-card h2 {
      font-size: 20px;
      font-weight: 600;
      margin: 0 0 8px;
    }
    .teams-auth-card p {
      font-size: 14px;
      color: var(--text-tertiary, #888);
      margin: 0 0 24px;
      line-height: 1.5;
    }
    .teams-auth-btn {
      display: inline-block;
      padding: 10px 24px;
      background: var(--accent, #6366f1);
      color: white;
      border: none;
      border-radius: 8px;
      text-decoration: none;
      font-size: 14px;
      font-weight: 600;
      cursor: pointer;
      font-family: inherit;
    }
    .teams-auth-btn:hover { filter: brightness(1.1); }
    .teams-auth-btn:disabled { opacity: 0.6; cursor: default; }
    .teams-auth-link {
      display: block;
      margin-top: 12px;
      font-size: 12px;
      color: var(--text-secondary, #999);
    }
    .teams-auth-diag {
      margin-top: 24px;
      text-align: left;
      font-size: 11px;
      color: var(--text-tertiary, #666);
    }
    .teams-auth-diag summary {
      cursor: pointer;
      font-size: 12px;
      color: var(--text-secondary, #999);
      margin-bottom: 8px;
    }
    .teams-auth-diag pre {
      background: var(--glass-tint, rgba(255,255,255,0.04));
      border: 1px solid var(--glass-border, rgba(255,255,255,0.08));
      border-radius: 6px;
      padding: 10px;
      font-size: 10px;
      font-family: 'JetBrains Mono', monospace;
      line-height: 1.6;
      max-height: 200px;
      overflow-y: auto;
      white-space: pre-wrap;
      word-break: break-all;
    }
  `,
})
export class App implements OnInit {
  protected readonly teamsAuth = inject(TeamsAuthService);
  private readonly teams = inject(TeamsService);
  private readonly _cateringBookingSync = inject(CateringBookingSyncService);
  private readonly posthog = inject(PosthogService);
  private readonly analytics = inject(AnalyticsService);
  private readonly consent = inject(ConsentService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly viewerService = inject(ViewerService);
  private readonly router = inject(Router);
  private readonly locationState = inject(LocationStateService);
  private readonly quickLinks = inject(QuickLinksService);
  private readonly calendarCapability = inject(CalendarCapabilityService);
  private readonly theme = inject(ThemeService);
  private readonly updater = inject(UpdaterService);
  private readonly desktopAuth = inject(DesktopAuthService);
  private readonly appUpdate = inject(AppUpdateService);
  // Constructed at bootstrap on purpose: it must be listening before the
  // browser fires the (once-only) beforeinstallprompt event.
  private readonly _pwaInstall = inject(PwaInstallService);
  private readonly regionSvc = inject(RegionService);
  // Handover §3. Constructed at bootstrap for the same reason
  // DivisionBrandingService is: its effect writes the record's custom
  // properties onto the token root, and a service nobody injects is a
  // service Angular never builds — the brand would then apply only on the
  // page that happened to inject it, which is precisely the failure §3 is
  // about. Injected here so every route is branded, not just one.
  private readonly _brandConfig = inject(BrandConfigService);

  /** One-shot guard for the signage auto-reload (never schedule twice). */
  private signageAutoReloadTimer: ReturnType<typeof setTimeout> | null = null;
  protected readonly loginUrl = `${environment.LOGIN_APP_URL}?app=${environment.APP}`;

  /** Interactive Teams sign-in in flight (failure-card button). */
  protected readonly teamsSigningIn = signal(false);

  /** User-gesture NAA sign-in from the failure card — the host brokers the
   *  auth dialog, then a reload re-runs boot with the stored tokens. */
  protected async teamsInteractiveSignIn(): Promise<void> {
    if (this.teamsSigningIn()) return;
    this.teamsSigningIn.set(true);
    try {
      const ok = await this.teamsAuth.signInInteractive();
      if (ok) window.location.reload();
    } finally {
      this.teamsSigningIn.set(false);
    }
  }

  /** Desktop first-run gate: show the welcome/region picker until a region is
   *  chosen. Static for the lifetime of this load (the welcome reloads the app
   *  after selection), so a plain signal snapshot is enough. */
  protected readonly showRegionWelcome = signal(regionGateActive());

  protected readonly isVendorRoute = signal(
    window.location.hash.startsWith('#/vendor'),
  );

  /** The old public design-system gallery URL, now a redirect that leaves
   *  for the Ricoh Paper docs site (core/guards/paper-docs-redirect.ts). It
   *  renders bare so a signed-out visitor following an old link never mounts
   *  the app shell, and its sign-in work, on the way out. */
  protected readonly isPaperDocsRedirectRoute = signal(
    window.location.hash.startsWith('#/design-system'),
  );

  /** Signage surfaces are full-bleed displays (kiosks, ScreenCloud iframes,
   *  the preview hub) — they render bare, with no app-shell chrome. */
  protected readonly isSignageRoute = signal(
    window.location.hash.startsWith('#/signage'),
  );

  /** Unattended signage PLAYER surfaces (kiosk loop / single full-screen
   *  surface) — nobody is at the keyboard to click an update prompt, so a
   *  newer deploy is applied automatically (see the auto-apply effect). */
  protected readonly isSignagePlayerRoute = signal(
    isUnattendedSignageUrl(window.location.hash),
  );

  /** Admin shell + its public kit gallery render full-bleed (no employee
   *  glass-bar chrome) — the admin area is built as a standalone, extractable
   *  lib with its own L-grid shell. */
  protected readonly isAdminRoute = signal(
    window.location.hash.startsWith('#/admin'),
  );

  /** The end of the sign-in cycle renders bare. It is the only thing the user
   *  can act on, and the shell's overlays (the onboarding tour among them)
   *  would cover the one screen that explains why nothing else will load. */
  protected readonly isSignInProblemRoute = signal(
    isSignInProblemSurface(window.location.hash),
  );

  /** Desktop browser hand-off screen — bare, public, API-silent (see
   *  core/desktop-auth.ts). */
  protected readonly isDesktopSignInRoute = signal(
    isDesktopSignInSurface(window.location.hash),
  );

  /** The desktop Flow widget window (flow_desktop.rs): one capsule, drawn from
   *  the main window's events. Bare, public, network-silent. */
  protected readonly isFlowWidgetRoute = signal(isFlowWidgetSurface());

  /** Public schedule-redesign preview renders bare + unauthenticated so the
   *  fidelity loop (Playwright) can screenshot it without a backend session.
   *  The authed in-app route (/schedule-preview) is unaffected. */
  protected readonly isSchedulePreviewRoute = signal(
    window.location.hash.startsWith('#/preview/schedule-redesign'),
  );

  /** The Access review harness renders the ADMIN surface — the employee glass
   *  chrome has no place over it, and it would sit on top of the harness bar. */
  protected readonly isAccessPreviewRoute = signal(
    window.location.hash.startsWith('#/preview/access'),
  );

  /** The map visual-style lab (and the directions, multi-day desk and
   *  map-louis harnesses) are full-bleed map surfaces. The glass bar would sit directly
   *  on top of their controls, and the shell's own viewer boot would fight
   *  their component-scoped map engine. */
  protected readonly isMapStylePreviewRoute = signal(
    /^#\/preview\/(map-(style|directions|louis)|desk-days)/.test(window.location.hash),
  );

  /** Outlook add-in task pane (and its preview harness) — a 320–450px
   *  docked pane inside Outlook. Renders bare: the employee shell's nav
   *  chrome has no place in a task pane. */
  protected readonly isAddinRoute = signal(isAddinUrl(window.location.hash.slice(1)));

  /** A hosted meeting's join page `/m/<code>` and its two harnesses: a door
   *  for guests too, so no employee shell, nav or onboarding over it. */
  protected readonly isFlowJoinRoute = signal(isFlowJoinBareRoute(window.location.hash));

  /** The Flow Brain lab: a full-screen dark canvas on synthetic data. No shell chrome over it. */
  protected readonly isFlowBrainRoute = signal(/^#\/(flow\/brain-lab|preview\/flow-brain)/.test(window.location.hash));

  /** Where the call dock may float: everywhere a signed-in person works, not on a door, a display or a pane. */
  protected readonly showCallDock = computed(
    () =>
      !this.showRegionWelcome() &&
      !this.isSignageRoute() &&
      !this.isFlowWidgetRoute() &&
      !this.isAddinRoute() &&
      !this.isFlowBrainRoute() &&
      !this.isFlowJoinRoute(),
  );

  /** Outside the app shell — applied with the same shape as isVendorRoute. */
  protected readonly isBareRoute = computed(
    () =>
      this.isVendorRoute() ||
      this.isPaperDocsRedirectRoute() ||
      this.isSignageRoute() ||
      this.isAdminRoute() ||
      this.isSchedulePreviewRoute() ||
      this.isAccessPreviewRoute() ||
      this.isMapStylePreviewRoute() ||
      this.isSignInProblemRoute() ||
      this.isDesktopSignInRoute() ||
      this.isFlowWidgetRoute() ||
      this.isAddinRoute() ||
      this.isFlowBrainRoute() ||
      this.isFlowJoinRoute(),
  );

  constructor() {
    const router = inject(Router);
    let lastFiredRoutePath: string | null = null;
    // No session replay on Flow pages (WP-49 part J): stopped on entering a
    // Flow route, resumed on leaving only if it was running before.
    const flowReplay = new FlowReplayGate({
      recording: () => this.analytics.sessionRecordingStarted(),
      stop: () => this.analytics.stopSessionRecording(),
      start: () => this.analytics.startSessionRecording(),
      optedOut: () => this.analytics.hasOptedOut(),
    });
    router.events.pipe(
      filter((e): e is NavigationEnd => e instanceof NavigationEnd),
    ).subscribe(e => {
      const url = e.urlAfterRedirects;
      flowReplay.onNavigate(url);
      this.isVendorRoute.set(url.startsWith('/vendor'));
      this.isPaperDocsRedirectRoute.set(url.startsWith('/design-system'));
      this.isSignageRoute.set(url.startsWith('/signage'));
      this.isSignagePlayerRoute.set(isUnattendedSignageUrl(url));
      this.isAdminRoute.set(url.startsWith('/admin'));
      this.isSignInProblemRoute.set(isSignInProblemSurface(url));
      this.isDesktopSignInRoute.set(isDesktopSignInSurface(url));
      this.isSchedulePreviewRoute.set(url.startsWith('/preview/schedule-redesign'));
      this.isAccessPreviewRoute.set(url.startsWith('/preview/access'));
      // The directions preview hosts its own map engine too.
      this.isMapStylePreviewRoute.set(/^\/preview\/(map-(style|directions|louis)|desk-days)/.test(url));
      this.isAddinRoute.set(isAddinUrl(url));
      this.isFlowJoinRoute.set(isFlowJoinBareRoute(url));
      this.isFlowBrainRoute.set(/^\/(flow\/brain-lab|preview\/flow-brain)/.test(url));
      // Dedupe on route_path so theme/picker/space-selection query
      // changes don't flood the Activity feed with near-duplicate
      // pageviews. PostHog Activity stays scannable; conversion funnels
      // stay accurate.
      const routePath = canonicalRoutePath(url);
      if (routePath === lastFiredRoutePath) return;
      lastFiredRoutePath = routePath;
      const domain = deriveDomainFromRoute(url);
      const routeName = deriveRouteName(url);
      // Promote to super-prop so EVERY subsequent event in this session
      // carries human-readable route context — the Activity column
      // becomes "Map" / "Catering · vendor" instead of a raw hash URL.
      this.analytics.register({
        route_path: routePath,
        route_name: routeName,
        domain,
      });
      this.analytics.pageview({
        $current_url: cleanCurrentUrl(window.location.href),
        route_path: routePath,
        route_name: routeName,
        domain,
      });
    });

    // Keep current_location_id on every event — re-register whenever the selected
    // location changes so downstream events get tagged with the right building
    // without each call site having to thread it through.
    effect(() => {
      const locId = this.locationState.selectedLocationId();
      this.analytics.register({ current_location_id: locId ?? null });
    });

    // Division id — the leaf-most division in the user's company hierarchy.
    // Mirrors how DivisionBrandingService derives the active division.
    // Also published as a PostHog `division` group so events can be
    // rolled up per division (alongside the `customer` group from
    // ViewerService) — gives "RICOH Europe IT vs RICOH UK Sales" cuts
    // without re-aggregating from person properties.
    let lastDivisionGroupId: string | null = null;
    effect(() => {
      const section = this.quickLinks.companySection();
      const divisions = section?.divisions;
      const leaf = divisions?.length ? divisions[divisions.length - 1]?.division : undefined;
      this.analytics.register({
        division_id: leaf?.id ?? null,
        division_name: leaf?.name ?? null,
      });
      if (leaf?.id && leaf.id !== lastDivisionGroupId) {
        lastDivisionGroupId = leaf.id;
        this.analytics.group('division', leaf.id, {
          name: leaf.name,
        });
      }
    });

    // Calendar provider — segment Graph vs Google vs none across every event.
    effect(() => {
      const provider = this.calendarCapability.calendarProvider();
      const hasCal = this.calendarCapability.hasCalendar();
      this.analytics.register({
        calendar_provider: provider ?? (hasCal ? 'unknown' : 'none'),
        calendar_status: this.calendarCapability.calendarStatus(),
      });
    });

    // Unattended signage displays can't click an update prompt — when a newer
    // deploy is detected on a PLAYER surface, reload onto it automatically
    // after a short randomised delay (jitter avoids a whole estate of screens
    // hammering the origin in the same second). One-shot: applyUpdate()
    // reloads the page, and if the user navigates away during the delay the
    // reload is still harmless (same route, fresh bundle).
    effect(() => {
      if (!this.appUpdate.updateAvailable()) return;
      if (!this.isSignagePlayerRoute()) return;
      if (this.signageAutoReloadTimer !== null) return;
      const delayMs = 5_000 + Math.random() * 10_000;
      this.signageAutoReloadTimer = setTimeout(
        () => void this.appUpdate.applyUpdate('signage-auto'),
        delayMs,
      );
    });
  }

  ngOnInit(): void {
    // Desktop-local boot — NO network. Runs even behind the region gate so the
    // welcome screen follows the OS appearance and console logs are captured.
    if (isTauri) {
      void attachTauriConsoleForwarding();
      void watchOsTheme((mode) => this.theme.applyOsAppearance(mode));
    }

    // The desktop Flow widget window boots the app for one capsule: no sign-in
    // listener (the main window owns the deep link), no analytics (the main
    // window records the widget's presses), no update checks.
    if (this.isFlowWidgetRoute()) return;

    // Data residency: until a region is chosen on the desktop welcome screen,
    // make NO backend or analytics calls to any region.
    if (this.showRegionWelcome()) return;

    // Desktop: listen for the ricohworkplace:// sign-in callback from the
    // system browser (network-silent until a handoff actually arrives).
    if (isTauri) void this.desktopAuth.start();

    // Session replay sampling — kept at 100% in both prod and dev while
    // the beta is small enough (low hundreds of users, ~4k events/week)
    // that quota isn't a concern. Every bug report ships with a replay,
    // and we can spot UX issues without waiting for the right session to
    // be sampled. Revisit when DAU climbs past ~500.
    const replaySampleRate = 1;

    const posthogOptions: Partial<PostHogConfig> = {
      api_host: environment.POSTHOG_HOST,
      ui_host: 'https://eu.posthog.com',
      defaults: '2026-01-30',
      capture_pageview: false,
      capture_exceptions: {
        capture_unhandled_errors: true,
        capture_unhandled_rejections: true,
        capture_console_errors: false,
      },
      rageclick: true,
      autocapture: { dom_event_allowlist: ['click', 'submit', 'change'] },
      // Drop low-signal autocapture rows before they ship to PostHog.
      // The Activity feed gets dominated by Mapbox canvas clicks (every
      // pan / zoom registers as "clicked canvas with aria label 'Map'")
      // — meaningful map clicks already fire as VIEWER_MARKER_CLICKED
      // with feature_kind + feature_id, so canvas autocapture is noise.
      // Same for surfaces marked .ph-no-capture (e.g. the bottom-nav dock,
      // covered by BOTTOM_NAV_SECTION_SELECTED).
      before_send: (event) => {
        if (!event) return event;
        // Backstop for Flow's no-replay rule (WP-49 part J): replay can start
        // just after the first navigation, so a snapshot chunk sent while the
        // page is a Flow page is dropped here too.
        if (typeof window !== 'undefined' && dropsReplaySnapshot(event.event, window.location.hash)) return null;
        // Signage links are bearer credentials in the URL hash. PostHog adds
        // `$current_url` to every event (and nested snapshot payloads), so
        // sanitise all strings before anything leaves the browser.
        // A hosted meeting's join code (`/m/<code>`) admits people, so it is
        // a credential in a URL too: redacted first, before anything else.
        const safeEvent = {
          ...event,
          // A hub's join link (`/hubs/join/<token>`, `?join=`) and an invite
          // id (`?invite=`) are redacted the same way (hub invites).
          properties: redactSignageTokens(redactHubJoinTokens(redactJoinCodes(event.properties))),
        };
        const properties = safeEvent.properties as
          | Record<string, unknown>
          | undefined;
        const currentRoute = canonicalRoutePath(
          typeof window !== 'undefined'
            ? window.location.hash.replace(/^#/, '').split('?')[0]
            : '',
        );
        if (currentRoute.startsWith('/preview/')) return null;
        const routePath = canonicalRoutePath(
          String(
          properties?.['route_path'] ??
            currentRoute,
          ),
        );
        // Preview harnesses use synthetic/mock data and must never pollute
        // product funnels, retention or first-use cohorts.
        if (routePath.startsWith('/preview/')) return null;
        if (properties) {
          properties['route_path'] = redactHubJoinTokenString(routePath);
          if (typeof properties['$current_url'] === 'string') {
            properties['$current_url'] = cleanCurrentUrl(
              properties['$current_url'],
            );
          }
        }
        const chain = String(properties?.['$elements_chain'] ?? '');
        // Masked for replay means never in analytics either (tv45.100): an
        // element event from inside `.ph-mask` or `.ph-block` is dropped,
        // whatever it is called. `chainNamesReplayMask` says why the chain
        // decides, and what it cannot see.
        if (chainNamesReplayMask(chain)) return null;
        if (safeEvent.event !== '$autocapture') return safeEvent;
        if (chain.includes('canvas.mapboxgl-canvas')) return null;
        if (chain.includes('ph-no-capture')) return null;
        return safeEvent;
      },
      // Dead-click detection — fires `$dead_click` when a user clicks an
      // element that produces no observable mutation/scroll/nav within ~2.5s.
      // Catches broken buttons before users have to tell us.
      enable_heatmaps: true,
      // posthog-js types lag the runtime feature flag — the option is
      // accepted at runtime in 1.180+ but only typed in newer minor
      // versions. Drop the assertion when posthog-js's PostHogConfig
      // catches up.
      // @ts-expect-error PostHogConfig type misses `dead_clicks_autocapture` for now
      dead_clicks_autocapture: true,
      session_recording: {
        maskAllInputs: true,
        maskTextSelector: '.ph-mask',
        // Health leaderboards include profile images as well as text. Blocking
        // the subtree keeps both DOM text and media URLs out of replay.
        blockSelector: '.ph-block',
      },
      // 25% baseline in prod; always record sessions that include an
      // exception so we can replay every error path end-to-end.
      session_recording_sample_rate: replaySampleRate,
      session_recording_minimum_duration_milliseconds: 5000,
      // A hosted meeting's join page is a door for guests, who have made no
      // analytics choice: opted out, no replay, nothing stored (P7.15 R10).
      // A signed-in employee there is opted back in by ViewerService.
      ...(isMeetingJoinSurface(typeof window !== 'undefined' ? window.location.hash : '')
        ? JOIN_SURFACE_POSTHOG_OPTIONS
        : {}),
      capture_performance: { web_vitals: true, network_timing: true },
      // SDK health log — visible in DevTools immediately, so when a flag
      // "isn't working" we can verify which distinct_id is in play and
      // whether the user is opted out before chasing ghosts. Gated to
      // non-production: distinct_id links to the person record and
      // session_id grants replay access, so we don't print them on a
      // shared/kiosk machine in prod (security audit P3).
      loaded: (ph) => {
        if (environment.production) return;
        console.info('[PostHog] loaded', {
          distinct_id: ph.get_distinct_id(),
          session_id: ph.get_session_id?.(),
          opted_out: ph.has_opted_out_capturing?.() ?? false,
        });
      },
    };

    // Tag every event with deployment context from second zero.
    const locale = (typeof navigator !== 'undefined' && navigator.language) || 'en-GB';
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
    const runtime: 'web' | 'teams' | 'tauri' =
      isTauri ? 'tauri' : this.teams.isInTeams() ? 'teams' : 'web';
    const registerDeploymentContext = (): void => this.analytics.register({
      app: 'emp',
      environment: environment.ENVIRONMENT,
      // Coarse tier alongside the exact label, so "is this real usage?" is one
      // stable filter that survives new regions and release channels.
      environment_tier: environmentTier(environment.ENVIRONMENT),
      app_version: environment.APP_VERSION,
      is_tauri: isTauri,
      runtime,
      locale,
      timezone,
      viewport_breakpoint: breakpointFor(window.innerWidth),
      // 'browser' in a tab; 'standalone' & friends when running as an
      // installed PWA — segments installed-app usage on every event.
      display_mode: currentDisplayMode(),
    });

    // PostHog only answers once it has started; before that every capture and
    // register goes to the no-op instance and is lost. Without the consent
    // banner it starts right here, so `whenAnalyticsStarted` runs its work at
    // once, in the order it always has. With the banner it starts once there
    // is a choice: the deployment context is registered inside the init, and
    // the work queued meanwhile runs once the choice has been applied to
    // PostHog (before that, `on_reject` mode would not send it), in order.
    const queued: (() => void)[] = [];
    let analyticsStarted = !environment.CONSENT_ENABLED;
    const whenAnalyticsStarted = (work: () => void): void => {
      if (analyticsStarted) work();
      else queued.push(work);
    };
    if (!environment.CONSENT_ENABLED) {
      this.posthog.init(environment.POSTHOG_TOKEN, posthogOptions);
      registerDeploymentContext();
    } else if (!isUnattendedSignageUrl(window.location.hash)) {
      this.startPostHogWithConsent(posthogOptions, registerDeploymentContext, () => {
        analyticsStarted = true;
        queued.splice(0).forEach((work) => work());
      });
    }
    // A signage PLAYER with the banner on: an unattended screen, where nobody
    // can make a choice, so no banner (app.config) and no PostHog at all.

    const handoff = parseSignageHandoffHash(window.location.hash);
    if (handoff) {
      const dedupeKey = signageHandoffDedupeKey(handoff);
      const pendingKey = `${dedupeKey}:auth-pending`;
      let alreadyCaptured = false;
      try {
        const pendingLogin = sessionStorage.getItem(pendingKey) === '1';
        if (pendingLogin) sessionStorage.removeItem(pendingKey);
        const lastCapture = Number(sessionStorage.getItem(dedupeKey) ?? 0);
        alreadyCaptured =
          pendingLogin ||
          (Number.isFinite(lastCapture) &&
            Date.now() - lastCapture < 30_000);
        if (!alreadyCaptured) {
          sessionStorage.setItem(dedupeKey, String(Date.now()));
        }
      } catch {
        /* storage unavailable — capture remains best effort */
      }
      if (!alreadyCaptured) {
        whenAnalyticsStarted(() => this.analytics.capture(ANALYTICS_EVENTS.SIGNAGE_QR_HANDOFF_OPENED, {
          target: handoff.target,
          screen_id: handoff.screenId,
        }));
      }
    }

    // ── Campaign / acquisition capture ────────────────
    // PostHog auto-captures `$initial_referrer`, `$initial_referring_domain`,
    // and UTM params as person properties, but only if they're present in
    // the URL at SDK init. We also lift them into session super-props so
    // EVERY event in this session carries the campaign axis (handy for
    // funnel/cohort filtering without joining back to person properties).
    // `registerOnce` $set_once's the *initial* values so re-entry doesn't
    // overwrite — important for "what acquired this user first?" questions.
    const campaign = readCampaignParams(window.location.search, document.referrer);
    if (campaign.hasAny) {
      whenAnalyticsStarted(() => {
        this.analytics.register(campaign.session);
        this.analytics.registerOnce(campaign.initial);
      });
    }

    // ── WebSocket preflight ───────────────────────────
    // A deterministic statement about the bundle and the page, before any
    // socket opens. The 19 Aug production build shipped an http:// WS URL: the
    // constructor threw as mixed content, graphql-ws never reached its own
    // error handler, and every subscription hung silently for three weeks
    // under `error_type: uncaught`. This names it in one event, once.
    const configuredWs = import.meta.env?.NG_APP_WS_URL;
    const wsProblem = wsConfigHealth(getWsUrl(), window.location.protocol, {
      source: getSelectedRegion()
        ? 'region'
        : configuredWs?.trim()
          ? 'env'
          : 'derived',
      configured: configuredWs,
    });
    if (wsProblem) {
      this.analytics.capture(ANALYTICS_EVENTS.REALTIME_WS_MISCONFIGURED, wsProblem);
      this.analytics.captureException(new Error(`[WS] ${wsProblem.problem}`), {
        error_type: 'websocket',
        ...wsProblem,
      });
    }
    // Lifecycle failures graphql-ws does see (rejected handshake, auth close,
    // drop): first per signature per session, then one summary every 25.
    const wsThrottle = createWsFailureThrottle();
    realtimeWsFailed$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (signal) => {
        const result = wsThrottle(signal);
        if (result?.report) {
          this.analytics.capture(ANALYTICS_EVENTS.REALTIME_WS_CONNECT_FAILED, result.props);
        }
      },
      // A ReplaySubject does not error; the handler exists so the analytics
      // bridge can never take the boot sequence down.
      error: (err: unknown) => console.warn('[WS] failure bridge:', err),
    });

    // Boot heartbeat — guaranteed first event after init, so we can see if the
    // SDK is wired up correctly even before the user does anything. Also lets
    // us measure time-to-app and segment by runtime/viewport in PostHog.
    const tta = Math.round(performance.now());
    const nav = (performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming | undefined);
    whenAnalyticsStarted(() => this.analytics.capture(ANALYTICS_EVENTS.APP_OPENED, {
      runtime,
      viewport_width: window.innerWidth,
      viewport_height: window.innerHeight,
      breakpoint: breakpointFor(window.innerWidth),
      locale,
      timezone,
      time_to_app_ms: tta,
      bfcache: nav?.type === 'back_forward',
    }));

    // Tab visibility — without this, sessions look like a single blob even when
    // the user is multi-tasking. Fires a focused/hidden pair so we can compute
    // active time per session in PostHog instead of just session duration.
    this.wireTabVisibilityEvents();
    // Identify as soon as the viewer query resolves, regardless of which
    // route boots first. Skips on the vendor portal and the other
    // intentionally unauthenticated surfaces (isUnauthenticatedSurface).
    //
    // Deferred until the FIRST navigation completes: the auth guard's token
    // bootstrap (hash-token exchange / refresh) runs during that navigation,
    // and firing the viewer query before it settles could 401 against a
    // stale bearer. The warm-up benefit is unchanged — route components
    // resolve after NavigationEnd too.
    const path = window.location.hash;
    const isUnauthSurface = isUnauthenticatedSurface(path);
    if (!isUnauthSurface) {
      this.router.events
        .pipe(
          filter((e): e is NavigationEnd => e instanceof NavigationEnd),
          take(1),
        )
        .subscribe({
          next: (event) => {
            // The initial hash can be guarded and still settle on a public
            // recovery route. Judge the completed navigation too, otherwise a
            // failed boot registers this warm-up at `#/` and fires `viewer`
            // after the guard redirects to `/sign-in-problem`.
            if (isUnauthenticatedSurface(`#${event.urlAfterRedirects}`)) return;
            void this.viewerService.ensureLoaded().catch(() => {});
          },
          // Router event streams don't error in practice; guard anyway so a
          // hypothetical failure can never take down the app bootstrap.
          error: () => {},
        });
    }

    // Desktop-only: silent update check shortly after boot (no UI unless an
    // install fails; inert until the updater is configured). Console forwarding
    // + OS-theme sync already ran at the top of ngOnInit (before the gate).
    if (isTauri) {
      setTimeout(() => void this.updater.check('boot'), 8000);
    }
    // Region super-property on every event + one-shot "region selected" funnel
    // event on the first boot after the welcome picker (the welcome screen
    // itself is network-silent, so the event fires here post-reload).
    if (isTauri) {
      const region = this.regionSvc.selected()?.id ?? null;
      const justSelected = this.regionSvc.takeJustSelected();
      whenAnalyticsStarted(() => {
        this.analytics.register({ region });
        if (justSelected) {
          this.analytics.capture(ANALYTICS_EVENTS.DESKTOP_WELCOME_REGION_SELECTED, {
            region: justSelected,
          });
        }
      });
    }
  }

  /**
   * With the cookie consent banner on (CONSENT_ENABLED): PostHog starts only
   * once there is a choice, because even in `on_reject` mode its start-up
   * fetches remote config and posts /flags with an anonymous id. A choice
   * made on login (the shared `.ricohworkplace.com` cookie) is already there,
   * so a returning person sees no second banner and PostHog starts at once.
   *
   * The app's own options are kept whole (before_send, the replay sample
   * rate, the meeting join page's `JOIN_SURFACE_POSTHOG_OPTIONS`); the package
   * adds cookieless capture on a rejection, starts with recording off and
   * installs our before_send behind its profile gate. Accepting opts PostHog
   * in and recording follows the Session recordings choice; the join page is
   * never recorded, so a guest's acceptance there only lets anonymous,
   * memory-only events flow. `registerContext` (the deployment
   * super-properties) runs inside the init, before any event; `onStarted`
   * once the choice has been applied, for the events queued meanwhile.
   *
   * Not awaited: it resolves only once there is a choice, and nothing on the
   * boot path may wait for the banner.
   */
  private startPostHogWithConsent(
    options: Partial<PostHogConfig>,
    registerContext: () => void,
    onStarted: () => void,
  ): void {
    const captureConsentChanged: ConsentListener = (state, { first }) =>
      this.analytics.capture(ANALYTICS_EVENTS.COOKIE_CONSENT_CHANGED, {
        analytics: state.analytics,
        recordings: state.recordings,
        reports: state.reports,
        revision: state.revision,
        first_choice: first,
        consent_id: state.consentId,
      });
    void this.consent.startPostHog(
      (beforeSend) => {
        this.posthog.init(environment.POSTHOG_TOKEN, {
          ...options,
          cookieless_mode: 'on_reject',
          disable_session_recording: true,
          before_send: beforeSend,
        });
        registerContext();
        return this.posthog.posthog;
      },
      {
        // A hosted meeting's join page is a door for guests: never recorded.
        recordingAllowed: !isMeetingJoinSurface(window.location.hash),
        beforeSend: options.before_send,
        afterChange: captureConsentChanged,
      },
    ).then((stop) => {
      // Null: the banner or the init failed (logged by the package), and
      // PostHog is the no-op instance; the queue has nowhere to go.
      if (stop) onStarted();
    });
  }

  private wireTabVisibilityEvents(): void {
    let lastVisibleAt = performance.now();
    let lastHiddenAt = 0;
    document.addEventListener('visibilitychange', () => {
      const now = performance.now();
      if (document.visibilityState === 'hidden') {
        const visibleFor = Math.round(now - lastVisibleAt);
        lastHiddenAt = now;
        this.analytics.capture(ANALYTICS_EVENTS.TAB_HIDDEN, { visible_for_ms: visibleFor });
      } else if (document.visibilityState === 'visible') {
        const hiddenFor = lastHiddenAt ? Math.round(now - lastHiddenAt) : 0;
        lastVisibleAt = now;
        this.analytics.capture(ANALYTICS_EVENTS.TAB_FOCUSED, { hidden_for_ms: hiddenFor });
      }
    });
  }

  /**
   * Keep the viewport_breakpoint super-prop in sync as the user resizes — without this,
   * a user who reloaded on mobile but is now on desktop still gets tagged 'xs'.
   * Throttled via the rAF chain so resize storms don't spam register().
   */
  @HostListener('window:resize')
  protected onWindowResize(): void {
    if (this._resizeQueued) return;
    this._resizeQueued = true;
    requestAnimationFrame(() => {
      this._resizeQueued = false;
      this.analytics.register({ viewport_breakpoint: breakpointFor(window.innerWidth) });
    });
  }
  private _resizeQueued = false;

  /**
   * Capture-phase click handler — when running in Tauri, intercept clicks on
   * external (http/https/mailto) anchor tags and route them through the OS
   * default handler instead of letting them navigate the webview to the
   * remote URL (which would replace the whole app). Web behaviour is
   * unchanged: same-origin clicks still navigate, target=_blank opens a
   * real new tab.
   */
  @HostListener('document:click', ['$event'])
  protected onDocumentClick(event: MouseEvent): void {
    if (!isTauri) return;
    if (event.defaultPrevented || event.button !== 0) return;
    if (event.metaKey || event.ctrlKey || event.altKey || event.shiftKey) return;
    const target = (event.target as HTMLElement | null)?.closest('a');
    if (!target) return;
    const href = target.getAttribute('href');
    if (!href) return;
    // Only intercept absolute http(s)/mailto/tel — let in-app SPA links pass.
    if (!/^(https?:|mailto:|tel:)/i.test(href)) return;
    event.preventDefault();
    void openExternal(href);
  }
}
